import type { Attachment, LinkPreview } from '@novachat/shared';
import { ChevronLeft, ChevronRight, Download, FileArchive, FileText, File as FileIcon, LoaderCircle, Maximize, Minus, Plus, X } from 'lucide-react';
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { useChannelImages } from '../lib/channel-media';
import { client } from '../lib/client';
import { fileUrl, sizeText } from '../lib/upload';

/** A signed link that renews itself once if it has expired (links last about a day). Follows `attachment` when it changes. */
function useFileLink(attachment: Attachment, variant: 'original' | 'preview') {
  const pick = (a: Attachment) => fileUrl(variant === 'preview' ? (a.previewUrl ?? a.url) : a.url);
  const [fresh, setFresh] = useState<Readonly<Record<string, string>>>({});
  const renewed = useRef(new Set<string>());
  const renew = () => {
    const id = attachment.id;
    if (renewed.current.has(id)) return;
    renewed.current.add(id);
    client.api
      .attachmentLink(id)
      .then((a) => setFresh((links) => ({ ...links, [id]: pick(a) })))
      .catch(() => {});
  };
  return [fresh[attachment.id] ?? pick(attachment), renew] as const;
}

const downloadUrl = (url: string) => `${url}${url.includes('?') ? '&' : '?'}dl=1`;

type Point = { x: number; y: number };
/** Zoom relative to the fitted size, and the pan from the centre in CSS pixels. */
type View = { scale: number; x: number; y: number; animate: boolean };

const FIT: View = { scale: 1, x: 0, y: 0, animate: true };
const MAX_SCALE = 8;
const STEP = 1.5;

const midpoint = (points: readonly Point[]): Point => ({
  x: points.reduce((sum, p) => sum + p.x, 0) / points.length,
  y: points.reduce((sum, p) => sum + p.y, 0) / points.length,
});
const distance = (points: readonly Point[]) => (points.length < 2 ? 0 : Math.hypot(points[0]!.x - points[1]!.x, points[0]!.y - points[1]!.y));
const clamp = (value: number, limit: number) => Math.min(limit, Math.max(-limit, value));

/** The gesture in progress: where it started, and what it has turned into so far. */
type Gesture = { start: View; mid: Point; dist: number; moved: boolean; pinched: boolean; onImage: boolean; touch: boolean; swipe: number | null };

type Side = 'before' | 'after';

/** The ends of a list that grows while it is viewed: a channel's images, oldest first, loaded a page at a time. */
export interface ImageListEnds {
  /** Whether more images may come before the first / after the last one; `null` while that is not known yet. */
  before: boolean | null;
  after: boolean | null;
  /** The end being loaded. */
  loading: Side | null;
  load: (side: Side) => void;
}

/**
 * "4 / 12" once both ends of the list are known. While older images may still load, only the place counted
 * from the newest one is certain ("Sondan 3."); with neither end known (still looking), nothing.
 */
function positionText(index: number, count: number, ends: ImageListEnds | undefined): string | null {
  if (!ends) return count > 1 ? `${index + 1} / ${count}` : null;
  if (ends.before === null || ends.after === null || (ends.before && ends.after)) return null;
  if (ends.before) return `Sondan ${count - index}.`;
  if (ends.after) return `${index + 1} / ${count}+`;
  return count > 1 ? `${index + 1} / ${count}` : null;
}

/**
 * Full-screen viewer for a list of images, starting at `start`. The image fits the screen and can be zoomed
 * (wheel, pinch, double click/tap, buttons) and dragged; ←/→, the side arrows or a swipe go through the list.
 * A fixed list goes round; one with `ends` stops at them, and loads more first where more may come.
 */
export function ImageViewer({ images, start = 0, ends, onClose }: { images: readonly Attachment[]; start?: number; ends?: ImageListEnds | undefined; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const picture = useRef<HTMLImageElement>(null);
  const count = images.length;
  // The image shown is followed by its id, so it stays put while images are added at either end.
  const [current, setCurrent] = useState(() => {
    const index = Math.min(Math.max(0, start), count - 1);
    return { id: images[index]?.id, index };
  });
  const found = images.findIndex((image) => image.id === current.id);
  const index = found >= 0 ? found : Math.min(current.index, count - 1);
  const image = images[index] ?? images[0]!;
  // The end an arrow, key or swipe went past, while the images beyond it load.
  const [waiting, setWaiting] = useState<Side | null>(null);
  const navigable = count > 1 || ends?.before === true || ends?.after === true;
  const [src, renew] = useFileLink(image, 'original');
  const [view, setView] = useState(FIT);
  // Handlers read the latest view from here; the state only drives rendering.
  const viewRef = useRef(FIT);
  const pointers = useRef(new Map<number, Point>());
  const gesture = useRef<Gesture | null>(null);
  const lastTap = useRef<{ time: number; x: number; y: number } | null>(null);

  useEffect(() => {
    dialog.current?.showModal();
    closeButton.current?.focus();
  }, []);

  const apply = (next: View) => {
    viewRef.current = next;
    setView(next);
  };

  /** A zoomed image always covers the stage on the long side; one smaller than the stage stays centred. */
  const settle = (next: View): View => {
    const scale = Math.min(MAX_SCALE, Math.max(1, next.scale));
    const img = picture.current;
    const box = stage.current;
    if (scale === 1 || !img || !box) return { ...next, scale, x: 0, y: 0 };
    const maxX = Math.max(0, (img.offsetWidth * scale - box.clientWidth) / 2);
    const maxY = Math.max(0, (img.offsetHeight * scale - box.clientHeight) / 2);
    return { ...next, scale, x: clamp(next.x, maxX), y: clamp(next.y, maxY) };
  };

  /** Zoom to `scale`, keeping the image point under (clientX, clientY) where it is; the centre by default. */
  const zoomTo = (scale: number, animate: boolean, at?: Point) => {
    const from = viewRef.current;
    const box = stage.current?.getBoundingClientRect();
    const target = Math.min(MAX_SCALE, Math.max(1, scale));
    const cx = box && at ? at.x - (box.left + box.width / 2) : 0;
    const cy = box && at ? at.y - (box.top + box.height / 2) : 0;
    const k = target / from.scale;
    apply(settle({ scale: target, x: cx - (cx - from.x) * k, y: cy - (cy - from.y) * k, animate }));
  };

  const show = (next: number) => {
    setCurrent({ id: images[next]!.id, index: next });
    setWaiting(null);
    apply(FIT);
  };

  const canGo = (side: Side) => (side === 'before' ? index > 0 : index < count - 1) || (ends ? ends[side] !== false : count > 1);

  /** One image back or forward. Past the end of a growing list it waits for the next images to load. */
  const go = (step: -1 | 1) => {
    const next = index + step;
    if (next >= 0 && next < count) return show(next);
    if (!ends) {
      if (count > 1) show((next + count) % count);
      return;
    }
    const side = step < 0 ? 'before' : 'after';
    if (ends[side] === false) return;
    setWaiting(side);
    if (ends[side]) ends.load(side);
  };

  // Moves on once the images beyond the end have come; stops waiting when none will.
  useEffect(() => {
    if (!waiting) return;
    const next = index + (waiting === 'before' ? -1 : 1);
    if (next >= 0 && next < count) show(next);
    else if (!ends || ends[waiting] === false || (ends[waiting] && ends.loading !== waiting)) setWaiting(null);
  }, [waiting, index, count, ends?.before, ends?.after, ends?.loading, !ends]);

  // React listens to wheel passively; this one must stop the page from zooming on a trackpad pinch (ctrl+wheel).
  const zoomRef = useRef(zoomTo);
  zoomRef.current = zoomTo;
  useEffect(() => {
    const box = stage.current;
    if (!box) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY * (e.deltaMode === 1 ? 40 : e.deltaMode === 2 ? 800 : 1);
      zoomRef.current(viewRef.current.scale * Math.exp(-delta * (e.ctrlKey ? 0.01 : 0.002)), false, { x: e.clientX, y: e.clientY });
    };
    box.addEventListener('wheel', onWheel, { passive: false });
    return () => box.removeEventListener('wheel', onWheel);
  }, []);

  /** Starts measuring again from the fingers now down, so adding or lifting one never makes the image jump. */
  const rebase = (from: Gesture | null, extra: Partial<Gesture>) => {
    const points = [...pointers.current.values()];
    gesture.current = {
      start: viewRef.current,
      mid: midpoint(points),
      dist: distance(points),
      moved: from?.moved ?? false,
      pinched: from?.pinched ?? false,
      onImage: from?.onImage ?? false,
      touch: from?.touch ?? false,
      swipe: null,
      ...extra,
    };
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const first = pointers.current.size === 0;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (first) rebase(null, { onImage: e.target === picture.current, touch: e.pointerType !== 'mouse' });
    // A second finger makes it a pinch: never a tap, never a swipe.
    else rebase(gesture.current, { moved: true, pinched: true });
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const g = gesture.current;
    if (!g || !pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const points = [...pointers.current.values()];
    const mid = midpoint(points);
    const dx = mid.x - g.mid.x;
    const dy = mid.y - g.mid.y;
    if (!g.moved && Math.hypot(dx, dy) < 6) return;
    g.moved = true;
    if (points.length >= 2 && g.dist > 0) {
      const box = stage.current?.getBoundingClientRect();
      if (!box) return;
      const scale = Math.min(MAX_SCALE, Math.max(1, (g.start.scale * distance(points)) / g.dist));
      const k = scale / g.start.scale;
      const cx = box.left + box.width / 2;
      const cy = box.top + box.height / 2;
      apply(settle({ scale, x: mid.x - cx - (g.mid.x - cx - g.start.x) * k, y: mid.y - cy - (g.mid.y - cy - g.start.y) * k, animate: false }));
    } else if (g.start.scale > 1) {
      apply(settle({ ...g.start, x: g.start.x + dx, y: g.start.y + dy, animate: false }));
    } else if (g.touch && !g.pinched && navigable) {
      // The fitted image follows the finger sideways; letting go far enough goes to the next one.
      g.swipe = dx;
      apply({ ...FIT, x: dx, animate: false });
    }
  };

  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    if (!pointers.current.delete(e.pointerId)) return;
    const g = gesture.current;
    if (pointers.current.size > 0) return rebase(g, {});
    gesture.current = null;
    if (!g) return;
    if (e.type === 'pointercancel') {
      if (g.swipe !== null) apply(FIT);
      return;
    }
    if (g.swipe !== null) {
      // Far enough goes on; otherwise (or at an end) the image slides back.
      const width = stage.current?.clientWidth ?? 0;
      apply(FIT);
      if (Math.abs(g.swipe) > Math.max(48, width * 0.15)) go(g.swipe < 0 ? 1 : -1);
      return;
    }
    if (g.moved) return;
    // A tap beside the image closes, like a click on the dark backdrop; two quick taps on it zoom.
    if (!g.onImage) return dialog.current?.close();
    const tap = { time: e.timeStamp, x: e.clientX, y: e.clientY };
    const last = lastTap.current;
    if (last && tap.time - last.time < 350 && Math.hypot(tap.x - last.x, tap.y - last.y) < 30) {
      lastTap.current = null;
      if (viewRef.current.scale > 1) apply(FIT);
      else zoomTo(2, true, tap);
    } else lastTap.current = tap;
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDialogElement>) => {
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    const actions: Record<string, () => void> = {
      ArrowLeft: () => go(-1),
      ArrowRight: () => go(1),
      '+': () => zoomTo(viewRef.current.scale * STEP, true),
      '=': () => zoomTo(viewRef.current.scale * STEP, true),
      '-': () => zoomTo(viewRef.current.scale / STEP, true),
      '0': () => apply(FIT),
    };
    const action = actions[e.key];
    if (!action) return;
    e.preventDefault();
    action();
  };

  const roundButton = 'grid size-9 place-items-center rounded-sm border-0 bg-white/10 text-white hover:bg-white/20';
  const sideButton = 'pointer-events-auto absolute top-1/2 grid size-11 -translate-y-1/2 place-items-center rounded-full border-0 bg-black/55 text-white';
  const position = positionText(index, count, ends);
  return (
    <dialog
      ref={dialog}
      onClose={onClose}
      onKeyDown={onKeyDown}
      onClick={(e) => e.target === dialog.current && dialog.current?.close()}
      aria-label={image.fileName}
      className="m-0 h-dvh max-h-none w-screen max-w-none bg-transparent p-0 backdrop:bg-black/85"
    >
      <div className="pointer-events-none flex h-full flex-col">
        <div className="pointer-events-auto flex items-center gap-3 px-4 pt-[max(12px,env(safe-area-inset-top))] pb-3 text-sm text-white">
          <span className="min-w-0 flex-1 truncate font-semibold">{image.fileName}</span>
          {position && (
            <span aria-live="polite" title={ends?.before ? 'En yeni görselden sayılır' : undefined} className="flex-none text-white/80 tabular-nums">
              {position}
            </span>
          )}
          <span className="text-white/60 max-sm:hidden">{sizeText(image.size)}</span>
          <a href={downloadUrl(src)} className="flex h-9 items-center gap-1.5 rounded-sm bg-white/10 px-3 font-semibold text-white hover:bg-white/20">
            <Download size={16} aria-hidden="true" />
            İndir
          </a>
          <button ref={closeButton} type="button" aria-label="Kapat" onClick={() => dialog.current?.close()} className={roundButton}>
            <X size={18} />
          </button>
        </div>
        <div className="relative min-h-0 flex-1">
          <div
            ref={stage}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            className={`pointer-events-auto absolute inset-0 touch-none overflow-hidden select-none ${view.scale > 1 ? 'cursor-grab active:cursor-grabbing' : ''}`}
          >
            {/* The frame leaves a margin, and room for the arrows on wider screens; the image fits inside it. */}
            <div className={`absolute inset-3 ${navigable ? 'sm:inset-x-16' : ''}`}>
              <img
                key={image.id}
                ref={picture}
                src={src}
                onError={renew}
                alt={image.fileName}
                draggable={false}
                className={`absolute inset-0 m-auto max-h-full max-w-full ${view.animate ? 'motion-safe:transition-transform motion-safe:duration-200' : ''}`}
                style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }}
              />
            </div>
          </div>
          {/* At an end the arrow is only marked unavailable, so a focused arrow keeps the focus (and the keys). */}
          {navigable &&
            (['before', 'after'] as const).map((side) => {
              const enabled = canGo(side);
              const Icon = waiting === side ? LoaderCircle : side === 'before' ? ChevronLeft : ChevronRight;
              return (
                <button
                  key={side}
                  type="button"
                  aria-label={side === 'before' ? 'Önceki görsel' : 'Sonraki görsel'}
                  aria-disabled={!enabled}
                  aria-busy={waiting === side}
                  onClick={() => go(side === 'before' ? -1 : 1)}
                  className={`${sideButton} ${side === 'before' ? 'left-3' : 'right-3'} ${enabled ? 'hover:bg-black/75' : 'cursor-default opacity-35'}`}
                >
                  <Icon size={24} className={waiting === side ? 'motion-safe:animate-spin' : undefined} />
                </button>
              );
            })}
        </div>
        <div className="flex justify-center px-4 pt-2 pb-[max(12px,env(safe-area-inset-bottom))]">
          <div className="pointer-events-auto flex items-center gap-1 rounded-sm bg-black/40 p-1 text-white">
            <button type="button" aria-label="Uzaklaştır" onClick={() => zoomTo(viewRef.current.scale / STEP, true)} className={roundButton}>
              <Minus size={18} />
            </button>
            <span className="w-12 text-center text-xs text-white/80 tabular-nums" aria-hidden="true">
              {Math.round(view.scale * 100)}%
            </span>
            <button type="button" aria-label="Yakınlaştır" onClick={() => zoomTo(viewRef.current.scale * STEP, true)} className={roundButton}>
              <Plus size={18} />
            </button>
            <button type="button" aria-label="Sığdır" onClick={() => apply(FIT)} className={roundButton}>
              <Maximize size={16} />
            </button>
          </div>
        </div>
      </div>
    </dialog>
  );
}

function ImageTile({ attachment, single, onOpen }: { attachment: Attachment; single: boolean; onOpen: () => void }) {
  const [src, renew] = useFileLink(attachment, 'preview');
  const width = attachment.width ?? 4;
  const height = attachment.height ?? 3;
  // One image keeps its shape (at most 400×320); several share a tidy square grid.
  const shown = single ? Math.min(400, width, (320 * width) / height) : undefined;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`${attachment.fileName} görselini büyüt`}
      className="block overflow-hidden rounded-md border border-line bg-surface-2 p-0"
      style={single ? { width: shown, maxWidth: '100%', aspectRatio: `${width} / ${height}` } : { aspectRatio: '1' }}
    >
      <img src={src} onError={renew} alt={attachment.fileName} loading="lazy" decoding="async" className="size-full object-cover" />
    </button>
  );
}

function Media({ attachment }: { attachment: Attachment }) {
  const [src, renew] = useFileLink(attachment, 'original');
  if (attachment.kind === 'audio') return <audio controls preload="none" src={src} onError={renew} className="w-full max-w-[400px]" />;
  return <video controls preload="metadata" playsInline src={src} onError={renew} className="max-h-[360px] w-full max-w-[480px] rounded-md border border-line bg-black" />;
}

function FileCard({ attachment }: { attachment: Attachment }) {
  const [src] = useFileLink(attachment, 'original');
  const Icon = /zip|rar|7z|tar|gzip/.test(attachment.mime) ? FileArchive : /pdf|text|word|document|sheet|presentation/.test(attachment.mime) ? FileText : FileIcon;
  return (
    <a
      href={downloadUrl(src)}
      className="flex max-w-[380px] items-center gap-3 rounded-md border border-line bg-surface-2 px-3 py-2.5 text-fg-1 hover:border-line-strong"
    >
      <span className="grid size-10 flex-none place-items-center rounded-sm bg-indigo-400/20 text-indigo-200">
        <Icon size={20} aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold">{attachment.fileName}</span>
        <span className="block text-xs text-fg-3">{sizeText(attachment.size)}</span>
      </span>
      <Download size={18} className="flex-none text-fg-2" aria-label="İndir" />
    </a>
  );
}

/** A card for a linked page; its picture comes from our server, never from the site itself. */
export function LinkCards({ links }: { links: readonly LinkPreview[] }) {
  if (links.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-col gap-1.5">
      {links.map((link) => (
        <a
          key={link.url}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex max-w-[460px] gap-3 rounded-md border border-l-[3px] border-line border-l-teal-400 bg-surface-2 p-3 hover:border-line-strong"
        >
          <span className="min-w-0 flex-1">
            {link.siteName && <span className="block truncate text-xs text-fg-3">{link.siteName}</span>}
            <span className="block text-sm font-semibold text-teal-400 [overflow-wrap:anywhere]">{link.title}</span>
            {link.description && <span className="mt-0.5 line-clamp-2 block text-[13px] text-fg-2">{link.description}</span>}
          </span>
          {link.imageUrl && <img src={fileUrl(link.imageUrl)} alt="" loading="lazy" className="size-16 flex-none rounded-sm object-cover" />}
        </a>
      ))}
    </div>
  );
}

/** The viewer over all of a channel's images (see useChannelImages), opened at `start`, one of `own`: its message's images. */
export function ChannelImageViewer({
  channelId,
  messageId,
  start,
  own,
  onClose,
}: {
  channelId: string;
  messageId: string;
  start: Attachment;
  own: readonly Attachment[];
  onClose: () => void;
}) {
  const { images, ends } = useChannelImages(channelId, messageId, start, own);
  return <ImageViewer images={images} start={images.findIndex((image) => image.id === start.id)} ends={ends} onClose={onClose} />;
}

/** Where a message is: its images open among all of the channel's. Thread replies and unsent messages have none. */
export type AttachmentsSource = { channelId: string; messageId: string };

/** Files under a message: images first (one large or a grid), then players and download cards. */
export function Attachments({ items, source }: { items: readonly Attachment[]; source?: AttachmentsSource | undefined }) {
  const [viewing, setViewing] = useState<number | null>(null);
  if (items.length === 0) return null;
  const images = items.filter((a) => a.kind === 'image');
  const others = items.filter((a) => a.kind !== 'image');
  return (
    <div className="mt-1.5 flex flex-col gap-1.5">
      {images.length > 0 && (
        <div className={images.length === 1 ? '' : 'grid max-w-[420px] grid-cols-2 gap-1.5'}>
          {images.map((a, i) => (
            <ImageTile key={a.id} attachment={a} single={images.length === 1} onOpen={() => setViewing(i)} />
          ))}
        </div>
      )}
      {others.map((a) => (a.kind === 'file' ? <FileCard key={a.id} attachment={a} /> : <Media key={a.id} attachment={a} />))}
      {viewing !== null &&
        images[viewing] &&
        (source ? (
          <ChannelImageViewer channelId={source.channelId} messageId={source.messageId} start={images[viewing]} own={images} onClose={() => setViewing(null)} />
        ) : (
          <ImageViewer images={images} start={viewing} onClose={() => setViewing(null)} />
        ))}
    </div>
  );
}
